<script setup>
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  assistants: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['action']);

const { t } = useI18n();
const router = useRouter();

const formatDate = dateString => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const handleEdit = assistant => {
  router.push({
    name: 'captain_assistants_edit',
    params: { assistantId: assistant.id },
  });
};

const handleViewInboxes = assistant => {
  router.push({
    name: 'captain_assistants_inboxes_index',
    params: { assistantId: assistant.id },
  });
};

const handleDelete = assistant => {
  emit('action', { action: 'delete', id: assistant.id });
};
</script>

<template>
  <div
    class="overflow-hidden border rounded-lg border-n-weak/70 bg-n-solid-1 shadow-sm min-h-[400px]"
  >
    <table class="w-full">
      <thead class="bg-n-alpha-1/50 border-b border-n-weak/60">
        <tr>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-bot size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.ASSISTANTS.TABLE.NAME') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-text size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.ASSISTANTS.TABLE.DESCRIPTION') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-calendar size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.ASSISTANTS.TABLE.CREATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-left tracking-wide text-n-slate-11"
          >
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-clock size-3.5 opacity-40" />
              <span>{{ t('CAPTAIN.ASSISTANTS.TABLE.UPDATED') }}</span>
            </div>
          </th>
          <th
            class="px-5 py-3 text-xs font-semibold text-right tracking-wide text-n-slate-11"
          >
            <div class="flex items-center justify-end gap-1.5">
              <span>{{ t('CAPTAIN.ASSISTANTS.TABLE.ACTIONS') }}</span>
              <span class="i-lucide-more-horizontal size-3.5 opacity-30" />
            </div>
          </th>
        </tr>
      </thead>
      <tbody class="bg-n-solid-1">
        <tr
          v-for="assistant in assistants"
          :key="assistant.id"
          class="transition-all duration-150 ease-out border-t border-n-weak/40 hover:bg-n-blue-2/50 group"
        >
          <td class="px-5 py-3">
            <div class="min-w-0">
              <span class="text-sm font-medium text-n-slate-12 block">
                {{ assistant.name }}
              </span>
            </div>
          </td>
          <td class="px-5 py-3">
            <span class="text-sm text-n-slate-10 line-clamp-2">
              {{ assistant.description || '-' }}
            </span>
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{ formatDate(assistant.createdAt || assistant.created_at) }}
          </td>
          <td class="px-5 py-3 text-sm text-n-slate-10">
            {{
              formatDate(
                assistant.updatedAt ||
                  assistant.updated_at ||
                  assistant.createdAt ||
                  assistant.created_at
              )
            }}
          </td>
          <td class="px-5 py-3">
            <div class="flex justify-end gap-2">
              <Button
                icon="i-lucide-inbox"
                variant="ghost"
                size="sm"
                color="slate"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.ASSISTANTS.TABLE.VIEW_INBOXES')"
                @click="handleViewInboxes(assistant)"
              />
              <Button
                icon="i-lucide-edit"
                variant="ghost"
                size="sm"
                color="blue"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.ASSISTANTS.TABLE.EDIT')"
                @click="handleEdit(assistant)"
              />
              <Button
                icon="i-lucide-trash-2"
                variant="ghost"
                size="sm"
                color="ruby"
                class="!w-8 !h-8"
                :title="t('CAPTAIN.ASSISTANTS.TABLE.DELETE')"
                @click="handleDelete(assistant)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
